/**
 * Login Page - Lenders App (Phase 1)
 * Simple login form for pre-created lender users
 * NO registration link - users are created by VeraScore team
 */

import { useState, useEffect, useRef } from "react";
import useAppState from "../../common/AppState";
import LoginApi from "../../api/LoginApi";
import { ProfileApiResponse } from "../../api/Types";
import "./Login.css";

// Helper to navigate using URL params (no react-router)
const navigateToDashboard = () => {
  window.history.replaceState({}, "", "?view=dashboard");
  window.dispatchEvent(new PopStateEvent("popstate"));
};

// Email validation helper
function isEmailValid(email: string): boolean {
  if (email === "") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function isSubmitDisabled(isLoading: boolean, email: string, password: string): boolean {
  if (isLoading) return true;
  if (!isEmailValid(email)) return true;
  if (password === "") return true;
  return false;
}

const Login: React.FC = () => {
  const didRedirect = useRef(false);

  // App state
  const isAuthenticated = useAppState((state) => state.isAuthenticated);
  const profile = useAppState((state) => state.profile);
  const setProfileData = useAppState((state) => state.setProfileData);
  const clearProfileData = useAppState((state) => state.clearProfileData);
  const clearLogoutFlag = useAppState((state) => state.clearLogoutFlag);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timeoutMessage, setTimeoutMessage] = useState<string | null>(null);

  const buttonDisabled = isSubmitDisabled(isLoading, email, password);

  // Check for timeout message on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reason = urlParams.get("reason");
    if (reason === "timeout") {
      setTimeoutMessage("Your session expired due to inactivity. Please log in again.");
      // Clear the reason from URL
      urlParams.delete("reason");
      window.history.replaceState({}, "", `?${urlParams.toString()}`);
    }
  }, []);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    // Check if we're on the login view
    const urlParams = new URLSearchParams(window.location.search);
    const currentView = urlParams.get("view");
    if (currentView !== "login") return;

    // Check if user just logged out
    const justLoggedOut = urlParams.get("logout") === "true";
    if (justLoggedOut) {
      window.history.replaceState({}, "", "?view=login");
      return;
    }

    const authLoaded = isAuthenticated !== "maybe";
    const profileLoaded = profile !== undefined && profile !== null;

    if (!authLoaded || !profileLoaded) return;
    if (didRedirect.current) return;
    if (isAuthenticated === "no") return;

    // Redirect authenticated users to dashboard
    if (import.meta.env.DEV) console.log("🔄 Login: User authenticated, redirecting to dashboard");
    didRedirect.current = true;
    navigateToDashboard();
  }, [isAuthenticated, profile]);

  // Login handlers
  function onLoginSuccess(data: ProfileApiResponse) {
    setProfileData(data);
    navigateToDashboard();
  }

  function onLoginFail(data: { reason?: string }) {
    clearProfileData();
    setIsLoading(false);
    setPassword("");
    setError(data.reason || "Login failed. Please check your credentials.");
  }

  function onLoginReject() {
    clearProfileData();
    setIsLoading(false);
    setPassword("");
    setError("Unable to connect. Please try again.");
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setIsLoading(true);
    clearLogoutFlag();
    LoginApi(email, password, onLoginSuccess, onLoginFail, onLoginReject);
  }

  // Show login form if not authenticated
  if (isAuthenticated === "no" || profile === undefined) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            {/* Header */}
            <div className="login-header">
              <p className="login-welcome">Welcome to</p>
              <img
                src="/VS-Logo_clear-bg.svg"
                alt="VeraScore Logo"
                className="login-logo"
              />
              <p className="login-subtitle">Lenders Portal</p>
            </div>

            {/* Timeout Message */}
            {timeoutMessage && (
              <div className="login-info" role="alert" style={{
                backgroundColor: '#fef3c7',
                borderColor: '#f59e0b',
                color: '#92400e',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: '1px solid'
              }}>
                <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>{timeoutMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="login-error" role="alert">
                <svg className="login-error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="login-form">
              {/* Email Field */}
              <div className="login-field">
                <label htmlFor="email" className="login-label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="username"
                  className="login-input"
                  required
                />
              </div>

              {/* Password Field */}
              <div className="login-field">
                <label htmlFor="password" className="login-label">
                  Password
                </label>
                <div className="login-password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="login-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="login-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="login-password-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg className="login-password-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="login-forgot">
                <a href="/forgot-password" className="login-forgot-link">
                  Forgot Password?
                </a>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={buttonDisabled}
                className="login-button"
              >
                {isLoading ? (
                  <span className="login-button-loading">
                    <svg className="login-spinner" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="login-footer">
              <p className="login-footer-text">
                Need access? Contact your VeraScore administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while redirecting
  return null;
};

export default Login;

